import React from 'react';
import PropTypes from 'prop-types';
import LocalStorage from 'storage/local';
import BaseTable from 'components/ui/table/Table';
import TableBodyCell from 'components/table/TableBodyCell';
import TableHeaderCell from 'components/table/TableHeaderCell';
import TableRow from 'components/ui/table/TableRow';
import compose from 'recompose/compose';
import styled from 'styled-components';
import theme from 'theme/theme';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { closeModal, openAlertModal } from 'store/actions/modalActions';
import { connect } from 'react-redux';

const StyledRow = styled(TableRow)`
    background-color: ${ (props) => {
        return props.backgroundCode;
    } };

    td {
        font-size: 13px;

        a {
            font-size: 13px;
            color: ${ (props) => {
                return props.colorCode;
            } };
        }
    }
`;

const DEFAULT_LINK = 'show';

class Table extends React.Component {
    constructor() {
        super();

        this.fetchColor = this.fetchColor.bind(this);
    }

    componentDidMount() {
        const {
            openModal,
            closeModal,
            translate,
            triggerInformationModal,
        } = this.props;

        if (triggerInformationModal) {
            const storage = new LocalStorage('list');

            if (!storage.get('firstVisit')) {
                openModal({
                    actions: {
                        submit: () => {
                            storage.set('firstVisit', true);

                            closeModal();
                        },
                    },
                    content: {
                        description: translate(`release.list.description`),
                    },
                    title: translate(`release.list.title`),
                });
            }
        }
    }

    updateSort = (event) => {
        event.stopPropagation();

        this.props.setSort(event.currentTarget.dataset.sort);
    };

    fetchBackground(id) {
        const {
            backgroundToggle,
            data,
        } = this.props;

        let result = 'transparant';

        if (backgroundToggle && Object.keys(backgroundToggle).length) {
            const record = data[id];

            if (!record) {
                return;
            }

            const {
                color,
                condition,
                key,
            } = backgroundToggle;

            if (record.hasOwnProperty(key)) {
                if (!!record[key] === condition) {
                    result = color;
                }
            }
        }

        return result;
    }

    fetchColor(id) {
        const {
            colorToggle,
            data,
        } = this.props;

        let result = theme.table.body.row.color;

        if (colorToggle && Object.keys(colorToggle).length) {
            const record = data[id];

            if (!record) {
                return;
            }

            const {
                color,
                condition,
                key,
            } = colorToggle;

            if (record.hasOwnProperty(key)) {
                if (!!record[key] === condition) {
                    result = color;
                }
            }
        }

        return result;
    }

    render() {
        const {
            children,
            currentSort,
            data,
            ids,
            responsive,
        } = this.props;

        return (
            <BaseTable
                responsive={ responsive }
            >
                <StyledRow>
                    { React.Children.map(children, (child, index) => {
                        if (utils.isEmpty(child) || utils.isEmpty(child.props)) {
                            return;
                        }

                        const {
                            column: {
                                align = 'left',
                                sortable = false,
                                width = 'auto',
                            },
                            source,
                        } = child.props;

                        return <TableHeaderCell
                            align={ align }
                            currentSort={ currentSort }
                            field={ child }
                            isSorting={ source === currentSort.field }
                            key={ source || index }
                            resource={ this.props.resource }
                            width={ width }
                            sortable={ sortable }
                            updateSort={ this.updateSort }
                        />;
                    }) }
                </StyledRow>

                { ids.map((id) => {
                    return (
                        <StyledRow
                            backgroundCode={ this.fetchBackground(id) }
                            colorCode={ this.fetchColor(id) }
                            key={ id }
                        >
                            { React.Children.map(children, (child) => {
                                if (utils.isEmpty(child) || utils.isEmpty(child.props)) {
                                    return;
                                }

                                const {
                                    column: {
                                        align = 'flex-start',
                                        link = DEFAULT_LINK,
                                    },
                                } = child.props;

                                return <TableBodyCell
                                    align={ align }
                                    field={ child }
                                    link={ link || '' }
                                    record={ data[id] }
                                    resource={ this.props.resource }
                                    source={ child.source || child.props.source }
                                />;
                            }) }
                        </StyledRow>
                    );
                })}
            </BaseTable>
        );
    }
}

Table.defaultProps = {
    data: {},
    ids: [],
    responsive: 'squish',
    triggerInformationModal: true,
};

Table.propTypes = {
    backgroundToggle: PropTypes.shape({
        color: PropTypes.string.isRequired,
        condition: PropTypes.bool.isRequired,
        key: PropTypes.string.isRequired,
    }),
    colorToggle: PropTypes.shape({
        color: PropTypes.string.isRequired,
        condition: PropTypes.bool.isRequired,
        key: PropTypes.string.isRequired,
    }),
    children: PropTypes.array,
    closeModal: PropTypes.func.isRequired,
    currentSort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.string,
        sort: PropTypes.string,
    }),
    data: PropTypes.object.isRequired,
    ids: PropTypes.arrayOf(PropTypes.any).isRequired,
    openModal: PropTypes.func.isRequired,
    resource: PropTypes.string,
    setSort: PropTypes.func,
    responsive: PropTypes.oneOf([
        'columns',
        'rows',
        'scroll',
        'squish',
    ]),
    triggerInformationModal: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => {
    return {
        openModal: (payload) => {
            dispatch(openAlertModal(payload));
        },
        closeModal: () => {
            dispatch(closeModal());
        },
    };
};

const enhance = compose(
    connect(
        null,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(Table);



// WEBPACK FOOTER //
// ./src/components/table/Table.js